/* ==========================================================================
   LV- Hero — Laverne Diamonds
   assets/lv-hero.js

   Custom element so the Shopify editor lifecycle is native.

   Responsibilities:
   - fade the video in only once it can genuinely play (poster stays until then)
   - never autoplay under prefers-reduced-motion, on save-data, or on phones
     when the merchant has opted out
   - pause when off-screen or when the tab is hidden, to save battery
   - optional sound toggle
   - one-shot reveal of the copy
   ========================================================================== */
(() => {
  'use strict';

  if (customElements.get('lv-hero')) return;

  const REDUCED = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const SAVE_DATA = () => {
    const c = navigator.connection;
    return !!(c && (c.saveData || /2g/.test(c.effectiveType || '')));
  };

  class LVHero extends HTMLElement {
    connectedCallback() {
      this.root = this.querySelector('[data-hero]');
      if (!this.root) return;

      this.video = this.root.querySelector('video');
      this.soundBtn = this.root.querySelector('[data-sound]');

      // Must run before setupVideo calls play(), otherwise the desktop file is
      // already being fetched by the time we swap it.
      this.swapMobileSource();

      this.reveal();
      this.setupVideo();
      this.setupSound();
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      if (this._vio) this._vio.disconnect();
      if (this._onVis) document.removeEventListener('visibilitychange', this._onVis);
    }

    /* ---- copy reveal ------------------------------------------------------ */
    reveal() {
      const items = this.root.querySelectorAll('[data-animate]');
      if (!items.length) return;

      if (!('IntersectionObserver' in window) || REDUCED()) {
        this.root.classList.add('is-in');
        return;
      }

      this.root.classList.add('anim-ready');

      // The hero is above the fold, so reveal on the next frame rather than
      // waiting for a scroll that may never happen.
      const r = this.root.getBoundingClientRect();
      if (r.top < innerHeight) {
        requestAnimationFrame(() => this.root.classList.add('is-in'));
        return;
      }

      this._io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          this.root.classList.add('is-in');
          this._io.disconnect();
        });
      }, { threshold: 0.15 });
      this._io.observe(this.root);
    }

    /* ---- mobile video source ----------------------------------------------
       When the merchant supplies both files, the desktop one is printed by
       Liquid and this rewrites the sources on small screens. preload is
       "metadata", so on a phone only a few KB of the desktop file can have
       been requested before the swap and the reload.
       ---------------------------------------------------------------------- */
    swapMobileSource() {
      const v = this.video;
      if (!v) return;
      const wrap = v.closest('[data-video-swap]');
      if (!wrap) return;
      const url = wrap.getAttribute('data-video-swap');
      if (!url || !matchMedia('(max-width: 749px)').matches) return;

      while (v.firstElementChild) v.removeChild(v.firstElementChild);
      const src = document.createElement('source');
      src.src = url;
      src.type = /\.webm(\?|$)/i.test(url) ? 'video/webm' : 'video/mp4';
      v.appendChild(src);
      v.load();
    }

    /* ---- video ------------------------------------------------------------ */
    setupVideo() {
      const v = this.video;
      if (!v) return;

      const mobileOff =
        this.dataset.videoMobile === 'false' && matchMedia('(max-width: 749px)').matches;

      if (REDUCED() || SAVE_DATA() || mobileOff) {
        // Poster only. Remove the sources so nothing is fetched at all.
        v.removeAttribute('autoplay');
        v.pause();
        v.remove();
        this.video = null;
        if (this.soundBtn) this.soundBtn.remove();
        return;
      }

      v.muted = true;          // required for autoplay everywhere
      v.playsInline = true;

      const show = () => this.root.classList.add('is-playing');
      if (v.readyState >= 3) show();
      v.addEventListener('canplay', show, { once: true });
      v.addEventListener('playing', show, { once: true });

      const tryPlay = () => {
        const p = v.play();
        if (p && p.catch) p.catch(() => { /* autoplay blocked: poster remains */ });
      };
      tryPlay();

      // Pause off-screen — a hero video running behind three sections of copy
      // is pure battery drain.
      if ('IntersectionObserver' in window) {
        this._vio = new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting) tryPlay();
            else v.pause();
          });
        }, { threshold: 0.1 });
        this._vio.observe(this.root);
      }

      this._onVis = () => {
        if (document.hidden) v.pause();
        else if (this.root.getBoundingClientRect().bottom > 0) tryPlay();
      };
      document.addEventListener('visibilitychange', this._onVis);
    }

    /* ---- sound ------------------------------------------------------------ */
    setupSound() {
      const btn = this.soundBtn;
      const v = this.video;
      if (!btn || !v) return;

      btn.addEventListener('click', () => {
        const on = v.muted;
        v.muted = !on;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (on) {
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
        }
      });
    }
  }

  customElements.define('lv-hero', LVHero);
})();
